import React from 'react';
import {
  Col,
  Form,
  FormItemProps,
  Input,
  Row,
  Typography,
  Tooltip,
} from 'antd';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextAreaProps } from 'antd/es/input';
import classNames from 'classnames';

type Props<T extends FieldValues> = {
  textAreaProps?: TextAreaProps;
  controller: UseControllerProps<T>;
  errorCol?: 12 | 24;
  showErrorTooltip?: boolean;
} & Omit<FormItemProps, 'children'>;

function TextAreaField<T extends FieldValues>(props: Props<T>) {
  const {
    textAreaProps,
    controller,
    errorCol = 24,
    label,
    showErrorTooltip = false,
    ...rest
  } = props;
  const { fieldState, field } = useController<T>(controller);
  const { error } = fieldState;

  const {
    className: textAreaClassName,
    style: textAreaStyle,
    ...restTextAreaProps
  } = textAreaProps || {};

  const showTooltip = showErrorTooltip && !!error;

  const textAreaNode = (
    <Input.TextArea
      {...restTextAreaProps}
      {...field}
      style={{ width: '100%', ...textAreaStyle }}
      className={classNames(textAreaClassName, {
        'form-tooltip-error': showTooltip,
      })}
    />
  );

  const fieldContent = showTooltip ? (
    <Tooltip
      placement="topLeft"
      title={error?.message}
      overlayClassName="form-tooltip-error-tooltip"
    >
      <span className="form-tooltip-wrapper">{textAreaNode}</span>
    </Tooltip>
  ) : (
    textAreaNode
  );

  return (
    <Form.Item
      colon={false}
      label={label}
      labelAlign="left"
      labelCol={label ? { span: 24 } : undefined}
      wrapperCol={label ? { span: 24 } : undefined}
      validateStatus={error ? 'error' : ''}
      {...rest}
    >
      <Row gutter={4}>
        <Col span={errorCol}>
          <Row gutter={8}>
            <Col span={24}>{fieldContent}</Col>
          </Row>
        </Col>
        {!showTooltip && (
          <Col span={errorCol}>
            {error && (
              <Typography.Text
                className="ant-form-item-explain ant-form-item-explain-error"
                type="danger"
                ellipsis={{ tooltip: true }}
                style={{ lineHeight: errorCol === 24 ? '16px' : '42px' }}
              >
                {error.message}
              </Typography.Text>
            )}
          </Col>
        )}
      </Row>
    </Form.Item>
  );
}

export default TextAreaField;
